// NOTE: While not ideal, the benefit of having a reusable query builder outweighs the cost of this import.
// The Oso team has plans to improve this and we can cross that bridge once we upgrade their SDK.
import {QueryArgs, QueryBuilder} from 'oso-cloud/dist/src/query';
import {
  PolarResources,
  PolarTypes,
} from '@packages/domains/identity-and-access/role-based-access/policy';

import type {
  OsoAction,
  OsoActor,
  OsoFact,
  OsoQuery,
  OsoResource,
  OsoResourceType,
  OsoUser,
} from './polar-extended';

// @see https://docs.google.com/document/d/1hLjTfE8XlzmRwO4MpTYUgYPnQLFnTkGnM7qfCm5STS8/edit?tab=t.0#heading=h.ub8orwtwbeys
export type Authorizations = {
  [R in OsoResourceType]?: AuthorizationData<R>;
};

export type AuthorizationData<R extends OsoResourceType = OsoResourceType> = {
  [resourceId in string]: {
    authorizedActions?: Array<OsoAction<R>>;
    isRestricted?: boolean;
  };
};

export type AuthorizationsByUser = {
  [UserId in string]: Authorizations;
};

export type AuthorizationsBySite = {
  [SiteId in string]: Authorizations;
};

// These are just proxies for the Oso types, but they provide a clean separation
// of concerns so that the AuthorizationService methods don't rely on Oso directly.
export type AuthorizationResourceType = OsoResourceType;
export type AuthorizationActor = OsoActor;
export type AuthorizationUser = OsoUser;
export type AuthorizationAction<
  R extends AuthorizationResourceType = AuthorizationResourceType,
> = OsoAction<R>;

export type AuthorizationResource<
  R extends AuthorizationResourceType = AuthorizationResourceType,
> = OsoResource<R> & {
  relationships?: AuthorizationFact[];
};
export type MaybeAuthorizationResource<
  R extends AuthorizationResourceType = AuthorizationResourceType,
> = {type: R; id?: string};
export type AuthorizationFact = OsoFact;

/**
 * Type representing has_relation facts specifically for Site resources.
 * Extracts all has_relation facts where the target resource is a Site.
 */
type SiteRelationAuthorizationFact = Extract<
  AuthorizationFact,
  [
    'has_relation',
    {type: AuthorizationResourceType; id: string},
    {type: 'String'; id: 'site'},
    {type: 'Site'; id: string},
  ]
>;

/**
 * Type representing the resource types that can have relations to Sites.
 * Extracts the source resource type from SiteHasRelationFact.
 */
export type SiteRelationAuthorizationResourceType =
  SiteRelationAuthorizationFact[1]['type'];

const OptInSiteResourceQueryTypes = ['SiteCollectionItem'] as const;

/**
 * Resource types that can be queried in a site context.
 * Includes types with a direct Polar site relation plus opted-in
 * resources that have indirect relationships with Sites,
 */
export type SiteResourceQueryType =
  | SiteRelationAuthorizationResourceType
  | (typeof OptInSiteResourceQueryTypes)[number];

// Resource types queryable in a site context: those with a direct Polar `site`
// relation, plus opted-in types that reach a Site indirectly (SiteCollectionItem
// via its collection). The opted-in literals must stay in sync with the
// SiteResourceQueryType union in ./authorization.
export const SiteResourceQueryTypes = new Set<string>([
  ...Object.keys(PolarResources).filter((key) => {
    const resource = PolarResources[key as keyof typeof PolarResources];
    return 'site' in resource.relations;
  }),
  ...OptInSiteResourceQueryTypes,
]);

// Query Types
type AuthorizationQuery = OsoQuery;
export type AuthorizationQueryArgs = QueryArgs<
  AuthorizationFact | AuthorizationQuery
>;
export type AuthorizationQueryBuilder = QueryBuilder<PolarTypes>;

export type ResourceContextValue = {
  [R in AuthorizationResourceType]?: Array<string>;
};
export type ResourceFilter = {
  types: Array<AuthorizationResourceType>;
};

/**
 * Array of resource types that support fine-grained access control.
 * Used across backend and frontend for consistent authorization checks.
 */
export const SITE_RESTRICTABLE_RESOURCE_TYPES = [
  'SiteCollection',
  'SiteLocale',
  'SitePage',
] as const;

/**
 * Union type representing resource types that can have fine-grained access
 * restrictions. Supports SiteCollection and SitePage resources, with potential
 * for expansion to other resource types.
 *
 * This type constrains which authorization resource types can be used with the
 * RestrictableResourceRepository, ensuring type safety for restriction
 * operations.
 */
export type RestrictableResourceType = AuthorizationResourceType &
  (typeof SITE_RESTRICTABLE_RESOURCE_TYPES)[number];

export function isRestrictableResourceType(
  resourceType: AuthorizationResourceType | string
): resourceType is RestrictableResourceType {
  return SITE_RESTRICTABLE_RESOURCE_TYPES.includes(
    resourceType as RestrictableResourceType
  );
}

const authorizationModes = [
  'legacy',
  'legacy-preferred',
  'oso-preferred',
  'oso',
] as const;

export type AuthorizationMode = (typeof authorizationModes)[number];

/**
 * Placeholder ID used for authorizing new resource creation.
 * When creating a new resource, the actual ID is server-generated after
 * authorization passes. This constant provides a consistent placeholder
 * that can be used with any resource type (e.g., SiteBranch, SitePage).
 *
 * The authorization check relies on the resource type to understand what
 * kind of new resource is being created.
 *
 * @example
 * // Authorizing new branch creation
 * getResource: () => ({
 *   type: 'SiteBranch' as const,
 *   id: NEW_RESOURCE_ID,
 * })
 */
export const NEW_RESOURCE_ID = 'new';

/**
 * Placeholder ID used for coarse-grained authorization checks where no specific
 * resource ID is available. This is common for virtual resources that are not
 * explicitly permissioned in our system (e.g., database-level operations that apply to all
 * collections on a site; the Admin tool). The authorization check relies on the resource type
 * and legacy options to determine access.
 *
 * NOTE: If endpoints use this ANY_RESOURCE_ID for authorization, they will need to change
 * this behavior if we implement fine-grained permissions for the resource type in question,
 * since ANY_RESOURCE_ID bypasses resource-specific checks and relies solely on the resource type.
 *
 * @example
 * // Authorizing access to any collection on a site
 * getResource: () => ({
 *   type: 'SiteCollection' as const,
 *   id: ANY_RESOURCE_ID,
 * })
 */
export const ANY_RESOURCE_ID = 'ANY';

/**
 * Common type guard for AuthorizationMode.
 *
 * @param mode
 * @returns
 */
export function isValidAuthorizationMode(
  mode: string
): mode is AuthorizationMode {
  return authorizationModes.includes(mode as AuthorizationMode);
}

export type AuthorizationModeResolutionReason =
  | 'code'
  | 'dynamic-config'
  | 'preserve-legacy-mode';

export function resolveAuthorizationMode({
  codeMode,
  dynamicConfigMode,
  preserveLegacyMode = false,
}: {
  codeMode: AuthorizationMode;
  dynamicConfigMode?: string | null;
  preserveLegacyMode?: boolean;
}): {
  mode: AuthorizationMode;
  reason: AuthorizationModeResolutionReason;
} {
  if (
    codeMode === 'oso' ||
    !dynamicConfigMode ||
    !isValidAuthorizationMode(dynamicConfigMode)
  ) {
    return {mode: codeMode, reason: 'code'};
  }

  if (
    preserveLegacyMode &&
    (codeMode === 'legacy' || codeMode === 'legacy-preferred')
  ) {
    if (dynamicConfigMode === 'legacy') {
      return {mode: 'legacy', reason: 'dynamic-config'};
    }

    if (
      codeMode === 'legacy' ||
      dynamicConfigMode === 'oso-preferred' ||
      dynamicConfigMode === 'oso'
    ) {
      return {mode: codeMode, reason: 'preserve-legacy-mode'};
    }
  }

  return {mode: dynamicConfigMode, reason: 'dynamic-config'};
}
